import { useSettings, useSettingActions } from "@/store/settingStore";
import { useEffect } from "react";
import { HtmlDataAttribute, ThemeColorPresets } from "#/enum";
import type { UILibraryAdapter } from "./type";
interface ThemeProviderProps {
	children: React.ReactNode;
	adapters?: UILibraryAdapter[];
}

export function ThemeProvider({ children, adapters = [] }: ThemeProviderProps) {
	const settings = useSettings();
	const { setSettings } = useSettingActions();
	const { themeMode, themeColorPresets, fontFamily, fontSize } = settings;

	// 自动迁移：如果当前是Default主题，自动切换到Blue主题（仅在首次加载时执行一次）
	useEffect(() => {
		if (themeColorPresets === ThemeColorPresets.Default) {
			setSettings({
				...settings,
				themeColorPresets: ThemeColorPresets.Blue,
			});
		}
		// biome-ignore lint/correctness/useExhaustiveDependencies: 这是初始化逻辑，只需要在首次加载时执行一次
	}, []);

	// Update HTML class to support Tailwind dark mode
	useEffect(() => {
		const root = window.document.documentElement;
		root.setAttribute(HtmlDataAttribute.ThemeMode, themeMode);
	}, [themeMode]);

	// Dynamically update theme color related CSS variables
	useEffect(() => {
		const root = window.document.documentElement;
		root.setAttribute(HtmlDataAttribute.ColorPalette, themeColorPresets);
	}, [themeColorPresets]);

	// Update font size and font family
	useEffect(() => {
		const root = window.document.documentElement;
		root.style.fontSize = `${fontSize}px`;

		const body = window.document.body;
		body.style.fontFamily = fontFamily;
	}, [fontFamily, fontSize]);

	// Wrap children with adapters
	const wrappedWithAdapters = adapters.reduce(
		(children, Adapter) => (
			<Adapter key={Adapter.name} mode={themeMode}>
				{children}
			</Adapter>
		),
		children
	);

	return wrappedWithAdapters;
}
